import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectUser, selectActivePixelStreamingExperience } from "../store/userSlice";
import { selectFeedbackData, selectConversationSessionSpec, selectSessionId } from "../store/sessionSlice";
import UserLogoutBar from "./UserLogoutBar";
import SidePanel, { SidePanelMode } from "./sidepanel/SidePanel";
import FinalFeedback from "./FinalFeedback";
import { sendEndOfSessionFeedback } from "../utils/sessionEndpoints";
import { useNavUtils } from "../utils/navUtils";
import logoImage from "tenant/logo.svg";
import "../styles/components/SessionEndedUniversal.css";

const SessionEndedUniversal: React.FC = () => {
  const { t } = useTranslation();
  const reduxUser = useSelector(selectUser);
  const feedbackData = useSelector(selectFeedbackData);
  const sessionId = useSelector(selectSessionId);
  const activeExperience = useSelector(selectActivePixelStreamingExperience);
  const sessionSpec = useSelector(selectConversationSessionSpec);
  const [showFeedback, setShowFeedback] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { logoutReset } = useNavUtils();

  const sharedFeedbackClicked = () => {
    console.log("Feedback link clicked!");
    setShowFeedback(true);
  };

  const goToNextStage = useCallback(async () => {
    if (activeExperience?.logout_user_post_experience === true) {
      await logoutReset();
    } else {
      setIsLoading(false);
    }
  }, [logoutReset, activeExperience]);

  const handleContinue = useCallback(async (skip: boolean) => {
    // Prevent multiple clicks
    if (isLoading) return;
    
    if (skip) {
      await goToNextStage();
      return;
    }

    console.log("Continue button clicked - Rating:", rating, "Feedback:", feedbackText, " sessionId:", sessionId, " sessionSpec:", sessionSpec);
    if (sessionId && sessionSpec) {
      setIsLoading(true);

      sendEndOfSessionFeedback(sessionSpec, sessionId, rating, feedbackText)
        .then(response => console.log("[SessionEndedUniversal] sendEndOfSessionFeedback response:", response))
        .catch(error => console.error("[SessionEndedUniversal] sendEndOfSessionFeedback error:", error))
        .finally(async () => {
          await goToNextStage();
        });
    }
  }, [logoutReset, sessionId, sessionSpec, rating, feedbackText, goToNextStage, isLoading]);

  const shouldShowFeedbackUI = activeExperience && activeExperience.requests_feedback_post_experience === true;

  if (shouldShowFeedbackUI) {
    return (
      <div className="session-ended-container">
        <div className="logo-container">
          <img src={logoImage} alt={t("sessionEnded.logoAlt")} />
        </div>
        <UserLogoutBar />
        <FinalFeedback
          rating={rating}
          setRating={setRating}
          feedbackText={feedbackText}
          setFeedbackText={setFeedbackText}
          isLoading={isLoading}
          onContinue={handleContinue}
        />
      </div>
    );
  }

  return (
    <div className="session-ended-container">
      <div className="logo-container">
        <img src={logoImage} alt={t("sessionEnded.logoAlt")} />
      </div>
      <UserLogoutBar />
      <div className="content">
        <div className="session-ended-main-text">{t("sessionEnded.thanks")}</div>
        <div className="session-ended-sub-text">{t("sessionEnded.requestSent")}</div>
        {reduxUser && (
          <div className="session-ended-share-text">
            {t("sessionEnded.haveAMoment")}{" "}
            <span 
              className="feedback-link"
              onClick={sharedFeedbackClicked}
            >
              {t("sessionEnded.shareFeedback")}
            </span>
            {" "}{t("sessionEnded.helpImprove")}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionEndedUniversal;
